/**
 * Bulletproof auth persistence.
 *
 * Storage strategy
 * ----------------
 * - `lex_access`  — JWT access token (24h TTL)
 * - `lex_refresh` — refresh token (30 day TTL)
 * - `lex_user`    — cached user object (for instant render on refresh)
 *
 * Why localStorage instead of just httpOnly cookies?
 * Cookies are blocked across origins when CORS isn't perfect (e.g. `*`
 * origin + credentials, www/non-www mismatch). Bearer-from-localStorage
 * works in every browser + CDN configuration.
 *
 * Refresh flow
 * ------------
 * 1. Mount → if `lex_access` exists, restore user from `lex_user` IMMEDIATELY
 *    (no flicker, no redirect to /login).
 * 2. Background-validate via /auth/me. On success: update cache. On 401:
 *    try POST /auth/refresh once. On still-401: clear all and sign out.
 * 3. Axios response interceptor catches any 401 on a non-auth call and
 *    silently runs /auth/refresh before retrying — exactly once per request.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

const ACCESS_KEY = "lex_access";
const REFRESH_KEY = "lex_refresh";
const USER_KEY = "lex_user";

// Helper — safe read/write that tolerates JSON parse errors.
const safeGetJSON = (key) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};
const safeSetJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private-mode — ignore */
  }
};

// Decode a JWT WITHOUT verifying signature — used only to check expiry
// client-side so we can decide whether to attempt /auth/me at all.
function decodeJwt(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(atob(payload))));
  } catch {
    return null;
  }
}

function jwtExpired(token) {
  const p = decodeJwt(token);
  if (!p || !p.exp) return true;
  return p.exp * 1000 < Date.now();
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 60000,
});

// Request interceptor — always attach Bearer from localStorage so we work
// across origins / CDNs without cookies.
api.interceptors.request.use((config) => {
  const tok = localStorage.getItem(ACCESS_KEY);
  if (tok && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// Response interceptor — on 401, run the refresh dance ONCE per request.
let refreshInFlight = null;
async function performRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) throw new Error("no refresh token");
    try {
      const { data } = await axios.post(
        `${API}/auth/refresh`,
        { refresh_token: refresh },
        { headers: { Authorization: `Bearer ${refresh}` }, withCredentials: true, timeout: 15000 },
      );
      if (data?.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
      if (data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
      if (data?.user) safeSetJSON(USER_KEY, data.user);
      return data;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    // Don't loop on the refresh endpoint itself.
    if (status === 401 && !original?.__retried && !url.includes("/auth/refresh") && !url.includes("/auth/login")) {
      original.__retried = true;
      try {
        const data = await performRefresh();
        if (data?.access_token) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${data.access_token}`;
          return api.request(original);
        }
      } catch {
        // Refresh failed → clear everything; AuthProvider will see user=null.
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    return Promise.reject(error);
  },
);

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }) {
  // Optimistic rehydration: if we have a cached user AND a non-expired
  // access token, render the protected UI IMMEDIATELY. /auth/me is then
  // fired in the background to keep the cache fresh.
  const initialUser = (() => {
    const tok = localStorage.getItem(ACCESS_KEY);
    if (!tok || jwtExpired(tok)) return null;
    return safeGetJSON(USER_KEY);
  })();
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState(!initialUser);
  const didInitialValidation = useRef(false);

  const persistUser = useCallback((u) => {
    if (u) safeSetJSON(USER_KEY, u);
    else localStorage.removeItem(USER_KEY);
    setUser(u);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      persistUser(data);
      return data;
    } catch (e) {
      // 401 already triggered the refresh-then-retry interceptor. If we
      // still got here, the refresh failed → genuinely logged out.
      if (e?.response?.status === 401) {
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
        persistUser(null);
      }
      return null;
    }
  }, [persistUser]);

  useEffect(() => {
    if (didInitialValidation.current) return;
    didInitialValidation.current = true;

    // CRITICAL: If returning from OAuth callback, skip the /me check;
    // AuthCallback handles it.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    const accessTok = localStorage.getItem(ACCESS_KEY);
    const refreshTok = localStorage.getItem(REFRESH_KEY);
    if (!accessTok && !refreshTok) {
      // No tokens at all — definitely signed out.
      setLoading(false);
      return;
    }

    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
    persistUser(data.user);
    return data.user;
  }, [persistUser]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
    if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
    persistUser(data.user);
    return data.user;
  }, [persistUser]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed; clearing client state anyway", e);
      }
    }
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    persistUser(null);
  }, [persistUser]);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser }),
    [user, loading, login, register, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
